import { useMemo, useCallback } from 'react';
import { useApiClient, useFetchInfinite } from '@marageti/z4-lib';
import { TicketComment, TicketCommentWithUploadURL } from '@marageti/z4-sdk/lib/tickets';
import putS3Document from '../../utils/s3-documents';

const COMMENTS_LIMIT = 20;

const useTicketComments = (ticketId?: string) => {
  const apiClient = useApiClient();

  const getKey = useCallback(
    (pageIndex: number, previousPageData: TicketComment[] | null) => {
      if (!ticketId) return null;
      if (previousPageData && previousPageData.length < COMMENTS_LIMIT) return null;
      const offset = previousPageData?.length
        ? previousPageData[previousPageData.length - 1].id
        : undefined;
      return ['getTicketComments', ticketId, COMMENTS_LIMIT, offset];
    },
    [ticketId],
  );

  const fetcher = useCallback(
    ([, id, limit, offset]: (string | number | undefined)[]) => (
      apiClient.ticketsClient.getTicketComments(id as string, {
        params: { limit, offset },
      })
    ),
    [apiClient],
  );

  const {
    data,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate,
  } = useFetchInfinite<TicketComment[]>(getKey, fetcher, {
    revalidateFirstPage: true,
    revalidateOnFocus: false,
  });

  const comments = useMemo(() => data?.flat() ?? [], [data]);

  const lastPage = data?.[data.length - 1];
  const hasMore = !lastPage || lastPage.length >= COMMENTS_LIMIT;

  const loadMore = useCallback(() => {
    if (!isValidating) setSize(size + 1);
  }, [isValidating, setSize, size]);

  const createComment = useCallback(
    async (text: string) => {
      if (!ticketId) return;
      await apiClient.ticketsClient.createTicketComment(ticketId, {
        type: 'COMMENT',
        text,
      });
      mutate();
    },
    [ticketId, apiClient, mutate],
  );

  const createFileComment = useCallback(
    async (file: File, title: string): Promise<TicketCommentWithUploadURL | null> => {
      if (!ticketId) return null;
      const result = await apiClient.ticketsClient.createTicketComment(ticketId, {
        type: 'FILE',
        fileName: title,
        document: {
          mediaType: file.type,
          sourceFileName: file.name,
          title,
        } as any,
      });
      if (result.preSignedURL?.url) {
        await putS3Document(result.preSignedURL.url, file);
      }
      mutate();
      return result;
    },
    [ticketId, apiClient, mutate],
  );

  const updateComment = useCallback(
    async (commentId: string, text: string) => {
      if (!ticketId) return;
      await apiClient.ticketsClient.updateTicketComment(ticketId, commentId, { text });
      mutate();
    },
    [ticketId, apiClient, mutate],
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (!ticketId) return;
      await apiClient.ticketsClient.deleteTicketComment(ticketId, commentId);
      mutate();
    },
    [ticketId, apiClient, mutate],
  );

  return {
    comments,
    isLoading: isLoading || isValidating,
    error: error ?? null,
    hasMore,
    loadMore,
    createComment,
    createFileComment,
    updateComment,
    deleteComment,
    mutate,
  };
};

export default useTicketComments;
